import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchStampStatus } from '../api/auth';
import { useQuery } from '@tanstack/react-query';
import type { StampStatus } from '../types';
import { useLocalization } from '../contexts/LocalizationContext';

function StampMini({ filled, total }: { filled: number; total: number }) {
  const stamps = Array.from({ length: total }, (_, i) => i < filled);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.ceil(total / 2)}, 1fr)`, gap: '8px' }}>
      {stamps.map((isFilled, i) => (
        <Box key={i} sx={{
          width: 32, height: 32, borderRadius: '50%', justifySelf: 'center',
          border: isFilled ? '1.5px solid #F27A3A' : '1.5px dashed #E0C4B4',
          background: isFilled ? 'linear-gradient(145deg,#F27A3A 0%,#E85A22 100%)' : 'rgba(255,255,255,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '10px', fontWeight: 700,
          color: isFilled ? '#fff' : '#C9A89A',
          boxShadow: isFilled ? '0 2px 6px rgba(232,90,34,0.25)' : 'none',
          flexShrink: 0,
        }}>
          {isFilled ? (
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M3.2 8.2l3 3.1 6.6-6.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (i + 1)}
        </Box>
      ))}
    </Box>
  );
}

export default function StampListPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();

  const { data: stampStatus } = useQuery<StampStatus>({
    queryKey: ['stampStatus'],
    queryFn: fetchStampStatus,
  });

  return (
    <Box sx={{ flex: 1, backgroundColor: '#F6F6F6', display: 'flex', flexDirection: 'column', pb: '30px' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 2, py: 1.5, position: 'sticky', top: 0,
        backgroundColor: '#fff', zIndex: 5, borderBottom: '1px solid #F0F0F0',
      }}>
        <Box
          onClick={() => navigate(-1)}
          sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 36, height: 36, ml: '-8px', cursor: 'pointer',
            borderRadius: '10px', fontSize: '28px', fontWeight: 300, lineHeight: 1, color: '#1A1A1A',
            '&:active': { backgroundColor: '#F5F5F5' },
          }}
        >‹</Box>
        <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1A1A1A' }}>{t('home.stamps.title')}</Typography>
      </Box>

      {/* Stamp list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', px: 2, pt: '4px', pb: '8px', mt: 1 }} role="list">
        {stampStatus ? (
          <Box
            component="button"
            type="button"
            role="listitem"
            sx={{
              display: 'block', width: '100%', textAlign: 'left',
              background: '#fff', border: '1px solid #E8E8E8', borderRadius: '14px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)', p: '16px',
              cursor: 'pointer', fontFamily: 'inherit',
              transition: 'transform 0.12s ease, background 0.12s ease',
              '&:active': { transform: 'scale(0.985)', background: '#FAFAFA' },
            }}
          >
            {/* Top row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', mb: '12px' }}>
              <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1A1A1A', lineHeight: 1.25 }}>
                  {t('home.stampCard')}
                </Typography>
                {stampStatus.rewardDesc && (
                  <Typography sx={{ mt: '3px', fontSize: '12px', color: '#8A8A8A', lineHeight: 1.35 }}>
                    {stampStatus.rewardDesc}
                  </Typography>
                )}
              </Box>
              <Box sx={{
                flexShrink: 0, fontSize: '12px', fontWeight: 700, color: '#E85A22',
                background: '#FDEEE4', borderRadius: '99px', px: '9px', py: '4px', lineHeight: 1,
              }}>
                {stampStatus.accumulatedStamps} / {stampStatus.stampsToRedeem}
              </Box>
            </Box>

            <StampMini filled={stampStatus.accumulatedStamps} total={stampStatus.stampsToRedeem} />

            {stampStatus.rewardDesc && (
              <Typography sx={{ mt: '10px', fontSize: '12px', color: '#666', lineHeight: 1.4 }}
                dangerouslySetInnerHTML={{ __html: t('common.moreStamps', {
                  count: Math.max(0, stampStatus.stampsToRedeem - stampStatus.accumulatedStamps),
                  reward: `<strong style="color:#6A9678;font-weight:700;">${stampStatus.rewardDesc}</strong>`,
                }) }}
              />
            )}
          </Box>
        ) : (
          <Typography sx={{ textAlign: 'center', py: 4, color: '#999', fontSize: '14px' }}>{t('home.stamps.empty')}</Typography>
        )}
      </Box>

    </Box>
  );
}
